import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useNavigate } from 'react-router-dom';

gsap.registerPlugin(ScrollTrigger);

const { FiTarget, FiCalendar, FiClock, FiCheck, FiArrowRight, FiPlus, FiMinus, FiChevronDown, FiHelpCircle, FiUsers, FiZap } = FiIcons;

const models = [
  {
    icon: FiTarget,
    title: "Fixed-Price Project",
    bestFor: "Well-defined projects with clear scope",
    desc: "You know what you need. We scope it, price it, and deliver it. No surprises, no scope creep. Ideal for ERP builds, specific software tools, and one-off AI integrations.",
    timeline: "4–16 weeks depending on scope",
    cta: "Get a Fixed Quote"
  },
  {
    icon: FiCalendar,
    title: "Monthly Retainer",
    bestFor: "Ongoing development, support, or AI maintenance",
    desc: "We become your extended tech team. A set number of hours per month, dedicated to building, improving, or maintaining your systems. Cancel anytime with 30 days notice.",
    timeline: "Ongoing, month-to-month",
    cta: "Discuss a Retainer"
  },
  {
    icon: FiUsers,
    title: "Staff Augmentation",
    bestFor: "Teams needing senior engineering capacity",
    desc: "Embed one or more of our senior engineers directly into your team. They work within your processes, tools, and workflows — but with our standard of engineering discipline.",
    timeline: "Minimum 1-month engagement",
    cta: "Augment Your Team"
  },
  {
    icon: FiZap,
    title: "Discovery Sprint",
    bestFor: "Scoping complex projects before committing",
    desc: "A focused 1–2 week engagement where we assess your current systems, define technical requirements, and deliver an architecture plan with clear cost and timeline estimates.",
    timeline: "1–2 weeks",
    cta: "Start a Discovery Sprint"
  },
  {
    icon: FiClock,
    title: "Hourly Consulting",
    bestFor: "Audits, architecture reviews, and short engagements",
    desc: "Need expert eyes on your existing system, a technology recommendation, or a one-day workshop? We offer structured consulting by the hour with no long-term commitment.",
    timeline: "Flexible, as-needed",
    cta: "Book a Session"
  }
];

const faqs = [
  {
    q: "Who owns the intellectual property and source code?",
    a: "You do. Upon final payment, all source code, documentation, and intellectual property produced during the engagement are transferred to you with full ownership rights. We retain no proprietary claim over your deliverables."
  },
  {
    q: "Do you sign NDAs?",
    a: "Always. Confidentiality is standard on every engagement we take on. We sign NDAs before any discovery call where sensitive information may be discussed."
  },
  {
    q: "What is your security posture?",
    a: "We follow OWASP best practices, encrypt data at rest and in transit, use role-based access controls, and conduct code reviews focused on security vulnerabilities. All cloud deployments follow Azure security benchmarks. We're happy to complete your security questionnaire."
  },
  {
    q: "How does handover work at the end of a project?",
    a: "We provide full source code, technical documentation, deployment guides, and a recorded walkthrough. We also offer a structured handover period (typically 2–4 weeks) where your internal team or new vendor can ask questions and get up to speed."
  },
  {
    q: "Do you work with international clients?",
    a: "Yes. We work with businesses in India, the United Kingdom, and remotely across the globe. Our workflows are built for async collaboration across time zones."
  },
  {
    q: "How long does a typical project take?",
    a: "Timelines vary by scope. A focused API or automation project can be completed in 2–4 weeks. A full ERP system typically takes 8–16 weeks. We provide detailed timelines during the discovery phase."
  },
  {
    q: "What happens after delivery?",
    a: "We offer post-delivery support, training, and retainer options so your system keeps running and improving long after launch. Most clients transition to a monthly retainer for ongoing maintenance and feature development."
  },
  {
    q: "What are your typical engagement models and budgets?",
    a: "We offer fixed-price projects (starting from mid four figures for focused builds), monthly retainers (flexible hours), and hourly consulting. Discovery sprints for scoping larger projects typically run 1–2 weeks. We'll recommend the right model during our initial conversation."
  }
];

const Engagement = () => {
  const navigate = useNavigate();
  const [activeFaq, setActiveFaq] = useState(null);

  const heroRef   = useRef(null);
  const cardsRef  = useRef([]);
  const ctaRef    = useRef(null);
  const push = (b) => (el) => { if (el) b.current.push(el); };

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(heroRef.current,
        { autoAlpha: 0, y: 28 },
        { autoAlpha: 1, y: 0, duration: 0.9, ease: 'expo.out' }
      );
      const cards = cardsRef.current.filter(Boolean);
      gsap.fromTo(cards,
        { autoAlpha: 0, y: 36, scale: 0.97 },
        { autoAlpha: 1, y: 0, scale: 1, duration: 0.7, ease: 'power3.out',
          stagger: { amount: 0.45, from: 'start' },
          scrollTrigger: { trigger: cards[0], start: 'top 84%', once: true } }
      );
      gsap.fromTo(ctaRef.current,
        { autoAlpha: 0, scale: 0.94 },
        { autoAlpha: 1, scale: 1, duration: 0.9, ease: 'expo.out',
          scrollTrigger: { trigger: ctaRef.current, start: 'top 85%', once: true } }
      );
    });
    return () => ctx.revert();
  }, []);

  return (
    <div className="pt-32 pb-0 relative overflow-hidden bg-white">
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.15]"></div>

      {/* Hero Header */}
      <section className="max-w-7xl mx-auto px-6 relative z-10 mb-20 text-center">
        <div ref={heroRef} style={{ opacity: 0 }}>
          <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            How We <span className="text-gradient-cyan">Work With You</span>
          </h1>
          <p className="text-slate-600 text-lg md:text-xl leading-relaxed max-w-3xl mx-auto">
            Every project is different. We offer three flexible engagement models so you can choose what fits your situation best.
          </p>
        </div>
      </section>

      {/* Engagement Cards */}
      <section className="max-w-7xl mx-auto px-6 relative z-10 mb-32">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {models.map((model, index) => (
            <div
              key={index}
              ref={push(cardsRef)}
              style={{ opacity: 0 }}
              className="bg-white border border-slate-200 p-8 flex flex-col hover:border-cyan-300 transition-all group shadow-sm hover:shadow-md"
            >
              <div className="w-16 h-16 bg-slate-50 border border-slate-200 flex items-center justify-center mb-8 group-hover:border-cyan-300 transition-colors">
                <SafeIcon icon={model.icon} className="text-3xl text-cyan-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">{model.title}</h3>
              <div className="mb-6">
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-600 bg-cyan-50 px-3 py-1 border border-cyan-200">
                  Best For: {model.bestFor}
                </span>
              </div>
              <p className="text-slate-600 leading-relaxed mb-4 flex-grow">
                {model.desc}
              </p>
              {model.timeline && (
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-10">
                  <SafeIcon icon={FiClock} className="inline mr-2 text-cyan-500" />
                  {model.timeline}
                </p>
              )}
              <button
                onClick={() => navigate('/contact')}
                className="w-full py-4 bg-transparent border border-slate-200 text-slate-900 font-bold uppercase tracking-widest text-xs hover:bg-slate-900 hover:text-white transition-all flex items-center justify-center gap-2"
              >
                {model.cta}
                <SafeIcon icon={FiArrowRight} />
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Discovery Call Section */}
      <section className="max-w-5xl mx-auto px-6 relative z-10 mb-32">
        <div ref={ctaRef} style={{ opacity: 0 }} className="bg-white border border-slate-200 p-12 text-center relative overflow-hidden shadow-sm">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 to-blue-500"></div>
          <h2 className="text-3xl font-bold text-slate-900 mb-6">Not sure which fits?</h2>
          <p className="text-slate-600 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
            Start with a free 30-minute discovery call. We'll listen to your situation and recommend the right approach — no sales pitch, no obligation.
          </p>
          <a
            href="#"
            className="inline-flex items-center gap-3 bg-slate-900 text-white px-10 py-5 font-black uppercase tracking-widest hover:bg-black transition-all group shadow-lg shadow-slate-200"
          >
            <span>Book a Free Discovery Call</span>
            <SafeIcon icon={FiCalendar} className="text-xl group-hover:rotate-12 transition-transform" />
          </a>
        </div>
      </section>

      {/* FAQ Strip */}
      <section className="bg-slate-50 border-y border-slate-100 py-24 relative z-10">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-12 justify-center">
            <SafeIcon icon={FiHelpCircle} className="text-2xl text-cyan-600" />
            <h2 className="text-2xl font-bold text-slate-900 uppercase tracking-widest">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-slate-200 bg-white">
                <button
                  onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                  className="w-full p-6 flex items-center justify-between text-left group"
                >
                  <span className="text-slate-900 font-bold tracking-wide">{faq.q}</span>
                  <div className={`transition-transform duration-300 ${activeFaq === index ? 'rotate-180 text-cyan-600' : 'text-slate-400'}`}>
                    <SafeIcon icon={FiChevronDown} />
                  </div>
                </button>
                <AnimatePresence>
                  {activeFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="p-6 pt-0 text-slate-600 leading-relaxed border-t border-slate-100">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="h-24"></div>
    </div>
  );
};

export default Engagement;